import { LoadingState } from '../../../../../@core/api/models';
import { InitiativesService } from '../../../../../@core/utils/initiatives.service';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NbCardModule, NbSpinnerModule, NbButtonModule, NbInputModule, NbSelectModule, NbOptionModule, NbAccordionModule, NbDialogRef, NbToggleModule, NbTabsetModule, NbRadioModule } from '@nebular/theme';
import { KPI } from '../../../../../@core/api';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { ScopeService, toLoadingState } from '../../../../../@core/utils/scope.service';
import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';
import { ProjectFormComponent } from '../project-form.component';
import { IntegerOnlyDirective } from '../../../../../@core/directives';

@Component({
  selector: 'app-project-gains-form',
  templateUrl: './project-gains-form.component.html',
  styleUrls: ['../../base-form/base-form.component.scss'],
  styles: [`
    .add-gain, [nbButton].appearance-ghost.status-basic:hover {
      background-color: #3a546b !important;
      color: #fff !important;
      width: 45px;
      height: 45px;
      border-radius: 50%;
      box-shadow: 0px 1px 3px 0px #1e2c39;
      position: absolute;
      right: 5px;
      top: 0;
      bottom: 0;
      margin: auto;
    }

    .add-gain:hover {
      color: #3a546b !important;
    }

    .accordion-button {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 30px;
      margin: auto;
      height: 30px;
      width: 30px;
      background-color: transparent !important;
      color: #222b45 !important;

      &:hover {
        color: #222b45 !important;
      }
    }

    nb-card-header {
      box-shadow: none !important;
      border-bottom: none !important;
    }

    :host ::ng-deep nb-radio label .text {
      font-weight: normal;
      font-family: var(--tabset-content-text-font-family);
      font-size: var(--tabset-content-text-font-size);
    }

  `],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbAccordionModule,
    AsyncPipe,
    TranslateModule,
    NgTemplateOutlet,
    NbToggleModule,
    NbTabsetModule,
    NgStyle,
    NbRadioModule,
    IntegerOnlyDirective,
  ],
})
export class ProjectGainsFormComponent implements OnInit, OnDestroy {

  @Input() projectForm: FormGroup;

  public title = this.translate.instant('Global gain of bouquet of works')
    + ' '
    +this.translate.instant('in')
    + ' '
    +this.translate.instant('kWh<sub>EUE</sub>');

  public energyTypes$: Observable<LoadingState<KPI.InitiativeEnergyType[]>> = this.initiativesService.initiativeEnergyTypes$.pipe(
    toLoadingState<KPI.InitiativeEnergyType[]>(),
  );
  public refrigerantTypes$: Observable<LoadingState<KPI.RefrigerantType[]>> = this.scopeService.refrigerantTypes$.pipe(
    toLoadingState<KPI.RefrigerantType[]>(),
  );

  constructor(
    private translate: TranslateService,
    private initiativesService: InitiativesService,
    private formBuilder: FormBuilder,
    private nbDialogRef: NbDialogRef<ProjectGainsFormComponent>,
    private scopeService: ScopeService,
  ) { }

  public ngOnInit(): void {
    if (!this.gains.length) {
      this.addGain();
    }
    this.initializeBackdropClick();
  }

  public ngOnDestroy(): void {
  }

  public get gains() {
    return this.projectForm.get("gains") as FormArray & {
      controls: FormGroup[];
    };
  }

  public get globalGain() {
    return this.projectForm.get("global_gain") as FormControl;
  }

  public addGain(): void {
    const gain = {
      id: null,
      energy_type: null,
      theoretical_savings_full_year: null,
      theoretical_savings_first_year: null,
      theoretical_greenhouse_gas_savings_full_year: null,
      theoretical_greenhouse_gas_savings_first_year: null,
    };

    this.gains.push(this.formBuilder.group(
      gain,
      { validators: [ProjectFormComponent.gainValidator(), Validators.required] },
    ));
  }

  public removeGain(index: number) {
    if (index === 0 && this.gains.length === 1) {
      this.gains.controls[0].reset();
    } else {
      this.gains.removeAt(index);
    }
  }

  /**
   * Remove empty gains from the form array
   * This is necessary because the form array is not updated when a gain is removed
   * Do it in reverse order to avoid index issues
   */
  private removeEmptyGains() {
    const controlsNb = this.gains.controls.length;
    const reversedIndexes = Array.from({ length: controlsNb }, (_, i) => i).reverse();
    reversedIndexes.forEach((index) => {
      if (this.gains.controls[index].invalid) {
        this.gains.removeAt(index);
      }
    });
  }

  private initializeBackdropClick(): void {
    // Replace the default backdrop click close event by a custom one
    this.nbDialogRef.onBackdropClick.pipe(take(1)).subscribe(() =>
      this.close(),
    );
  }

  public close(): void {
    this.removeEmptyGains();
    this.nbDialogRef.close();
  }
}
